import { AlertLabel } from '../AlertLabel';
import { ContentSection } from '../ContentSection';
import { usePlatformLink } from '../contexts';
import { Alerts } from '../StrokeIcons';

const WARNING_KEYS = ['custom', 'hubspot', 'slack'] as const;
type WarningKey = (typeof WARNING_KEYS)[number];

type WarningsProps = {
  userId: string;
  userTeamTimezoneName: string | null;
  warnings: Record<WarningKey, string | null>;
  emailCredentials: { primary: boolean }[] | undefined;
  isEmailCredentialsLoading: boolean;
};

export const Warnings = ({
  userId,
  userTeamTimezoneName,
  warnings,
  emailCredentials,
  isEmailCredentialsLoading,
}: WarningsProps) => {
  const Link = usePlatformLink();

  const hasPrimaryEmailCredential = emailCredentials?.some((c) => c.primary) ?? false;
  const shouldShowEmailWarning =
    !isEmailCredentialsLoading && (emailCredentials?.length ?? 0) > 0 && !hasPrimaryEmailCredential;

  const hasAnyWarning = WARNING_KEYS.some((k) => warnings[k] != null);
  if (!hasAnyWarning && !shouldShowEmailWarning) return null;

  return (
    <ContentSection size="small">
      {WARNING_KEYS.map((warningKey) => {
        const warningMessage = warnings[warningKey];
        if (!warningMessage) return null;

        const isSlack = warningKey === 'slack';
        const isHubspotMissingProperty = warningKey === 'hubspot' && warningMessage === 'missing_linkedin_property';
        const hasCustomMessage = isSlack || isHubspotMissingProperty;

        return (
          <AlertLabel key={warningKey} type="warning" icon={<Alerts.AlertTriangle />}>
            {isSlack && (
              <p>
                Please re-authenticate your slack app. Click here to visit{' '}
                <Link href="/teams/active?defaultId=slack#integration-section">Settings</Link>
              </p>
            )}
            {isHubspotMissingProperty && (
              <p>
                Please select linkedin fields in your hubspot integration. Click here to visit{' '}
                <Link href="/teams/active?defaultId=hubspot#integration-section">Settings</Link>
              </p>
            )}
            {!hasCustomMessage && warningMessage}
          </AlertLabel>
        );
      })}

      {userTeamTimezoneName == null && (
        <AlertLabel type="warning" icon={<Alerts.AlertTriangle />}>
          <p>
            Set your timezone in <Link href={`/teams/active/${userId}`}>settings</Link> to send messages at the right
            time.
          </p>
        </AlertLabel>
      )}

      {shouldShowEmailWarning && (
        <AlertLabel type="warning" icon={<Alerts.AlertTriangle />}>
          <p>
            We recently added a new feature that allows you to send emails from your Gmail account using app passwords.
            &nbsp;
            <Link href="/teams/active/">Click here</Link> to add email credentials & set one as primary.
          </p>
        </AlertLabel>
      )}
    </ContentSection>
  );
};
